// Aquí están los thunks relacionados con eventos
import { createAsyncThunk } from "@reduxjs/toolkit";
import store from "@/app/store";
import api from "@/services/api";
import { publicEventosSuccess, publicEventosFailure, appendPublicEventos } from "./publicEventosSlice";

/**
 * 📝 EVENTOS PÚBLICOS - Obtener eventos con filtrado, búsqueda y paginación
 * 
 * @param {Object} filters - Objeto con filtros opcionales
 * @param {string} filters.categoriaEvento - Categoría del evento (ej: "Música", "Teatro")
 * @param {string} filters.nombre - Búsqueda parcial por nombre del evento
 * @param {string} filters.fechaHoraInicioDesde - Fecha inicio desde (ISO datetime)
 * @param {string} filters.fechaHoraInicioHasta - Fecha inicio hasta (ISO datetime)
 * @param {string} filters.fechaHoraFinDesde - Fecha fin desde (ISO datetime)
 * @param {string} filters.fechaHoraFinHasta - Fecha fin hasta (ISO datetime)
 * @param {string} filters.sortBy - Campo para ordenar ('fechaHoraInicio', 'nombre', etc.)
 * @param {string} filters.sortOrder - Dirección ordenamiento ('asc' | 'desc')
 * @param {number} filters.page - Número de página (default: 1)
 * @param {number} filters.limit - Elementos por página (default: 10)
 */
export const getPublicEventosInfo = createAsyncThunk(
  "publicEventos/getPublicEventosInfo",
  async (filters = {}, { dispatch, rejectWithValue }) => {
    try {
      // Construir URLSearchParams solo con valores definidos
      const params = new URLSearchParams();
      
      // Filtro de categoría (omitir si es "Todos los Tickets")
      if (filters.categoriaEvento && filters.categoriaEvento !== 'Todos los Tickets') {
        params.append('categoriaEvento', filters.categoriaEvento);
      }
      
      // Filtro de búsqueda por nombre
      if (filters.nombre) {
        params.append('nombre', filters.nombre);
      }
      
      // Filtros de fecha de inicio
      if (filters.fechaHoraInicioDesde) {
        params.append('fechaHoraInicioDesde', filters.fechaHoraInicioDesde);
      }
      if (filters.fechaHoraInicioHasta) {
        params.append('fechaHoraInicioHasta', filters.fechaHoraInicioHasta);
      }
      
      // Filtros de fecha de fin
      if (filters.fechaHoraFinDesde) {
        params.append('fechaHoraFinDesde', filters.fechaHoraFinDesde);
      }
      if (filters.fechaHoraFinHasta) {
        params.append('fechaHoraFinHasta', filters.fechaHoraFinHasta);
      }
      
      // Parámetros de ordenamiento
      if (filters.sortBy) {
        params.append('sortBy', filters.sortBy);
      }
      if (filters.sortOrder) {
        params.append('sortOrder', filters.sortOrder);
      }
      
      // Paginación
      params.append('page', String(filters.page || 1));
      params.append('limit', String(filters.limit || 10));
      
      const { data } = await api.get(`/eventos?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(publicEventosSuccess({ 
        infoPublicEventos: data.eventos,
        pagination: data.pagination,
        filters: data.filters,
        sort: data.sort
      }));
      
      return data;      
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Error al obtener eventos";
      dispatch(publicEventosFailure(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * 📝 EVENTOS PÚBLICOS - Cargar más eventos (para paginación infinita)
 * 
 * Este thunk agrega eventos a la lista existente en lugar de reemplazarla.
 * Útil para "Load More" o infinite scroll en carruseles.
 */
export const loadMorePublicEventos = createAsyncThunk(
  "publicEventos/loadMorePublicEventos",
  async (filters = {}, { dispatch, rejectWithValue }) => {
    try {
      // Construir URLSearchParams
      const params = new URLSearchParams();
      
      if (filters.categoriaEvento && filters.categoriaEvento !== 'Todos los Tickets') {
        params.append('categoriaEvento', filters.categoriaEvento);
      }
      
      if (filters.nombre) {
        params.append('nombre', filters.nombre);
      }
      
      if (filters.fechaHoraInicioDesde) {
        params.append('fechaHoraInicioDesde', filters.fechaHoraInicioDesde);
      }
      if (filters.fechaHoraInicioHasta) {
        params.append('fechaHoraInicioHasta', filters.fechaHoraInicioHasta);
      }
      
      if (filters.fechaHoraFinDesde) {
        params.append('fechaHoraFinDesde', filters.fechaHoraFinDesde);
      }
      if (filters.fechaHoraFinHasta) {
        params.append('fechaHoraFinHasta', filters.fechaHoraFinHasta);
      }
      
      if (filters.sortBy) {
        params.append('sortBy', filters.sortBy);
      }
      if (filters.sortOrder) {
        params.append('sortOrder', filters.sortOrder);
      }
      
      params.append('page', String(filters.page || 1));
      params.append('limit', String(filters.limit || 10));
      
      const { data } = await api.get(`/eventos?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Usar action para agregar eventos en lugar de reemplazar
      dispatch(appendPublicEventos({ 
        eventos: data.eventos,
        pagination: data.pagination
      }));
      
      return data;      
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Error al cargar más eventos";
      return rejectWithValue(errorMessage);
    }
  }
);